import { IndeterminateCircleLoader, StatusIndicator } from 'cdk-radial';
import * as Styles from './styles';
import { useTranslation } from 'react-i18next';
import {MeetMyTeamList} from './MeetMyTeamList';
import {CMFNumber} from './CMFNumberComponent';
import {UserStores} from './UserStores';
import React,{useState,useTransition,useEffect,useMemo} from 'react';
import
  userProfileStore 
  from '../core/UserProfileStore';

import {useGetMeetMyCdkContant,useGetCMFNumber} from "./useMeetMyTeamQueries";
import { STATUS_INDICATOR_VARIANTS } from '../../constants';

type Positions = {
  top: string;
  bottom: string;
  left: string;
  right: string;
};

type FooterNoteContainerT = {
  children: React.ReactNode;
  position?: string;
  positionProps?: Partial<Positions>;
};

const FooterNoteContainer = ({
  children,
  position,
  positionProps,
}: FooterNoteContainerT): JSX.Element => {
  return (
    <Styles.StyledTextWithBadgeContainer
      positionProps={positionProps}
      position={position}
    >
      {children}
    </Styles.StyledTextWithBadgeContainer>
  );
};

const createOptions = (iamUserInfo:any) => 
  iamUserInfo?.fullUser?.stores?.map((store:any) => ({
      label: `${store.name} - ${store.id}`,
      value: store.id,
      isDefault: store.defaultStore,
    })) || [];

const findDefaultStore = (options:any) => options.find((store:any) => store.isDefault);

const getMyCdkTeamAccess =(unifyProfile:any) =>{
  return unifyProfile.featureAccess && 
                  ( unifyProfile.featureAccess.find(
                        (access:any) => access.name === "MyCdkTeam")?.isAccess ||
                        unifyProfile.featureAccess.find(
                          (access:any) => access.name === "MyCdkTeam")?.is_access);
};

function hasMessage(error: unknown): error is { message: string } {
  return (error as { message: string }).message !== undefined;
}

const MeetMyTeamContainer = () => {
  
  const [isPending, startTransition] = useTransition();
  const iamUserInfo = userProfileStore((state:any) => state.iamUserProfile);
  const unifyProfile = userProfileStore((state:any) => state.unifyUserProfile);
  const isAdmin = userProfileStore((state:any) => state.isEnterpriseAdmin);
  
  const { t } = useTranslation();

  const [selectedOption, setSelectedOption] = useState({ label: '' } as any);
  const [clear,setClear] = useState(false);
  
  const options = useMemo(() => {
    const stores = createOptions(iamUserInfo);
    return stores.sort((a:any, b:any) => a.label.localeCompare(b.label));
  }, [iamUserInfo]);

  const isMyCdkTeamAccess = getMyCdkTeamAccess(unifyProfile);

  const {data: teamData, status, error} = useGetMeetMyCdkContant(iamUserInfo?.fullUser?.enterprise?.id,selectedOption?.value,
    isAdmin || isMyCdkTeamAccess);
  
  const {data:cmfData ,status: cmfNumberApiStatus, error: cmfNumberApiError} = useGetCMFNumber(iamUserInfo?.fullUser?.enterprise?.id,selectedOption?.value);

  useEffect(() => {
    if (iamUserInfo) {
      startTransition(() => {
        const defaultStore = findDefaultStore(options);
        if (defaultStore) {
          setSelectedOption(defaultStore);
        }
      });
    }
  }, [iamUserInfo, options]);

  const handleSelectionChange = (store:string) => {
    setClear(false);
    startTransition(() => {
      const selected = options.find((option:any) => option.value === store);
      setSelectedOption(selected);
    });
  };

  const handleClear=() =>{
    setClear(true);
  }

  const MeetMyCDKContact = React.memo(({ data , isAdmin, isMyCdkTeamAccess} : any) => (
    <Styles.StyledCustomScrollbarWrapper>
      <Styles.Container>
        <MeetMyTeamList data={data} isAdmin ={isAdmin} isMyCdkTeamAccess={isMyCdkTeamAccess}/>
      </Styles.Container>
      {
      !isAdmin && !isMyCdkTeamAccess && data?.length > 0 && 
      <div style={{display:'flex',position:'absolute',margin:'0px 16px', top:'calc(100% - 120px)'}}>
        {t('meetmycdkteam.footerMsgForNonEAAdmin')}
      </div>
      }
    </Styles.StyledCustomScrollbarWrapper>
  ));

  return (
    <>
    {options.length === 0 && <div style={{ padding: '16px' }}>{t('meetmycdkteam.notAvailable')}</div>}
      {options.length > 1 && (
        <>
          <UserStores
            id="meet-my-team-auto-complete"
            defaultOptions={options}
            selected={selectedOption}
            onSelectionChange={handleSelectionChange}
            onClear={handleClear}
          />
        </>
      )}
      {!clear && (
        <div style={{ marginTop: '20px' }}>
          {cmfNumberApiStatus === 'error' ? (
            <div style={{ padding: '16px' }}>
              {hasMessage(cmfNumberApiError) ? cmfNumberApiError.message : 'An unknown error occurred while fetching CMF Number'}
              </div>
          ) : cmfNumberApiStatus === 'loading' || isPending ? (
            <div style={{marginLeft:'15px'}} >
              <IndeterminateCircleLoader label={t('meetmycdkteam.fetchCMFNumber')} size="small"/>
            </div>
          ) : (
            <CMFNumber data={cmfData} ></CMFNumber>
          )}
          {status === 'error' ? (
            <div style={{ padding: '16px' }}>
              {hasMessage(error) ? error.message : 'An unknown error occurred while fetching CDK Contacts'}
              </div>
          ) : status === 'loading' || isPending ? (
            <div style={{marginLeft:'15px'}} >
              <IndeterminateCircleLoader label={t('meetmycdkteam.fetchCDKContact')} size="small"/>
            </div>
          ) : (
            <MeetMyCDKContact data={teamData} isAdmin={isAdmin} isMyCdkTeamAccess={isMyCdkTeamAccess}/>
          )}
          {
            isAdmin &&
            <FooterNoteContainer position={'absolute'} positionProps={{ bottom: '0px' }}>
              <StatusIndicator label="Enterprise Admin Enabled" showIcon variant={STATUS_INDICATOR_VARIANTS.INFORMATION} />
              <Styles.FooterTextNote>
              {t('meetmycdkteam.footerMsgForEAAdmin')}
              </Styles.FooterTextNote>
            </FooterNoteContainer>
          }
        </div>
      )}
    </>
  );
};

export default MeetMyTeamContainer;
